import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * Sentio Quick-View Host
 *
 * Headless `<sentio-tool-bar>` element: it renders nothing visible, it only
 * hosts the quick-view popup. The tool grids (menu drawer, desktop Shop
 * dropdown, empty cart) and the homepage shop-category tiles request a quick
 * view via a document-level `sentio:tool-quickview` event; we clone the
 * matching <template> (host-scoped first, then document-wide for
 * section-hosted templates) into the modal <dialog> and add to the existing
 * cart via CartAddEvent.
 *
 * (The old scroll-driven floating bar was removed — only the popup remains.
 * Tag name stays `sentio-tool-bar` because the grids guard on it.)
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} popup
 * @property {HTMLElement} popupBody
 * @property {HTMLButtonElement} popupClose
 * @extends {Component<Refs>}
 */
class SentioToolBar extends Component {
  requiredRefs = ['popup', 'popupBody', 'popupClose'];

  /** External openers (the tool grids) request a quick view via a
      document-level event, so they don't need a reference to us. */
  #handleExternalQuickView = (e) => this.#openQuickView(e.detail && e.detail.index);

  connectedCallback() {
    super.connectedCallback();

    this.#setupPopup();
    document.addEventListener('sentio:tool-quickview', this.#handleExternalQuickView);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('sentio:tool-quickview', this.#handleExternalQuickView);
  }

  /* ---------------------------------------------------------------------- */
  /* Quick-view popup                                                       */
  /* ---------------------------------------------------------------------- */

  #setupPopup() {
    const { popup, popupBody, popupClose } = this.refs;

    popupClose.addEventListener('click', () => this.#closePopup());
    // Click on the backdrop (the dialog element itself) closes it.
    popup.addEventListener('click', (e) => {
      if (e.target === popup) this.#closePopup();
    });

    popupBody.addEventListener('click', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      const variant = target.closest('.sentio-qv__variant');
      if (variant instanceof HTMLElement) return this.#selectVariant(variant);
      const add = target.closest('[data-qv-add]');
      if (add instanceof HTMLElement) return this.#addToCart(add);
    });
  }

  #openQuickView(index) {
    if (index == null) return;
    // Host templates first (tool grids, indices 0-4), then document-wide so
    // sections can host their own templates (e.g. shop-category popups).
    const sel = `template[data-tool-qv="${index}"]`;
    const tpl = this.querySelector(sel) || document.querySelector(sel);
    if (!(tpl instanceof HTMLTemplateElement)) return;

    this.refs.popupBody.replaceChildren(tpl.content.cloneNode(true));

    const popup = this.refs.popup;
    if (typeof popup.showModal === 'function') popup.showModal();
    else popup.setAttribute('open', '');
  }

  #closePopup() {
    const popup = this.refs.popup;
    if (typeof popup.close === 'function') popup.close();
    else popup.removeAttribute('open');
  }

  #selectVariant(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const group = button.parentElement;
    if (group) {
      group.querySelectorAll('.sentio-qv__variant').forEach((b) => {
        const selected = b === button;
        b.classList.toggle('is-selected', selected);
        b.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
    }

    const body = this.refs.popupBody;
    const price = body.querySelector('[data-qv-price]');
    if (price && button.dataset.variantPrice) price.textContent = button.dataset.variantPrice;

    const add = body.querySelector('[data-qv-add]');
    if (add instanceof HTMLElement) {
      add.dataset.variantId = button.dataset.variantId || '';
      const available = button.dataset.variantAvailable === 'true';
      /** @type {HTMLButtonElement} */ (add).disabled = !available;
      const label = add.querySelector('[data-qv-add-label]');
      if (label) label.textContent = available ? 'Add to cart' : 'Sold out';
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Add to cart — mirrors product-form.js so the header + drawer update    */
  /* ---------------------------------------------------------------------- */

  async #addToCart(button) {
    const btn = /** @type {HTMLButtonElement} */ (button);
    if (btn.disabled || btn.dataset.loading) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const label = btn.querySelector('[data-qv-add-label]');
    btn.dataset.loading = '1';
    btn.classList.add('is-loading');
    if (label) label.textContent = 'Adding…';

    try {
      // routes.cart_add_url (e.g. "/cart/add") rendered onto the element; the
      // ".js" endpoint returns JSON including any requested section HTML.
      const url = `${this.dataset.cartAddUrl}.js`;

      const form = new FormData();
      form.append('id', variantId);
      form.append('quantity', '1');

      // Request the cart-drawer's section(s) so it can morph itself in place.
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter(Boolean);
      if (sectionIds.length) form.append('sections', sectionIds.join(','));

      const response = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: form,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to add to cart');
      }

      if (label) label.textContent = 'Added ✓';

      // source: 'product-form-component' tells cart-icon to ADD to the current
      // count (rather than overwrite it) — matching the theme's own flow.
      document.dispatchEvent(
        new CartAddEvent({}, 'sentio-tool-bar', {
          source: 'product-form-component',
          itemCount: 1,
          productId: btn.dataset.productId,
          variantId: variantId.toString(),
          sections: data.sections,
        })
      );

      setTimeout(() => this.#closePopup(), 700);
    } catch (error) {
      console.error('[sentio-tool-bar] add to cart failed:', error);
      if (label) label.textContent = 'Try again';
    } finally {
      delete btn.dataset.loading;
      btn.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('sentio-tool-bar')) {
  customElements.define('sentio-tool-bar', SentioToolBar);
}
